import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useAuth } from "../../contexts/AuthProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useUserShifts } from "../../hooks/useUserShifts";
import { backendService } from "../../services/backend.service";
import { AgentSource, type OpenShiftType, Preferences, ShiftType, type SpecificPreference, type WeeklyPreference } from "../../utils/models";
import { getShiftIndexByShiftTimes } from "../../utils/utilFunctions";
import type { SuggestedShift } from "./PickupSuggestionDrawer";

const SCORE_THRESHOLD = 0;
const MAX_SUGGESTIONS = 2;
const TWO_WEEKS_DAYS = 14;
const SUGGESTION_WINDOW_DAYS = 10;

/**
 * Checks if the shift date falls within any "avoid" specific preference or avoidDates.
 */
function isOnAvoidDate(
	shift: OpenShiftType,
	specificPreferences: SpecificPreference[],
	avoidDates: { date: DateTime; reason: string; type: string }[],
	timezone: string,
): boolean {
	const shiftDate = shift.startTime.setZone(timezone).startOf("day");

	// Check avoidDates
	if (avoidDates.some((ad) => ad.date.setZone(timezone).startOf("day").equals(shiftDate))) {
		return true;
	}

	// Check specificPreferences with type=Avoid
	const avoidPrefs = specificPreferences.filter((sp) => sp.type === Preferences.Avoid);
	return avoidPrefs.some((pref) => {
		const prefStart = pref.start_date.setZone(timezone).startOf("day");
		const prefEnd = pref.end_date.setZone(timezone).startOf("day");
		return shiftDate >= prefStart && shiftDate <= prefEnd;
	});
}

/**
 * Checks if the nurse has a weekly "avoid" preference for this day+shift combo.
 */
function isOnWeeklyAvoid(shift: OpenShiftType, weeklyPreferences: WeeklyPreference[], timezone: string): boolean {
	const shiftDate = shift.startTime.setZone(timezone);
	const dayName = shiftDate.toFormat("EEEE");

	return weeklyPreferences.some(
		(pref) =>
			pref.type === Preferences.Avoid &&
			pref.day.toLowerCase() === dayName.toLowerCase() &&
			(pref.shift.toLowerCase() === shift.type.toLowerCase() || pref.shift.toLowerCase() === "all"),
	);
}

/**
 * Checks if the shift overlaps with any of the nurse's existing shifts on that date.
 */
function hasScheduleConflict(shift: OpenShiftType, userShifts: Record<string, import("../../utils/models").NurseInShift[]>, timezone: string): boolean {
	const dateKey = shift.startTime.setZone(timezone).toFormat("yyyy-MM-dd");
	const existingShifts = userShifts[dateKey];
	if (!existingShifts || existingShifts.length === 0) return false;

	return existingShifts.some((existing) => {
		if (!existing.startTime || !existing.endTime) return false;
		// Overlap: shift starts before existing ends AND shift ends after existing starts
		return shift.startTime < existing.endTime && shift.endTime > existing.startTime;
	});
}

export function useNurseShiftScores(requestIds: string[], source: AgentSource) {
	return useQuery({
		queryKey: ["nurseShiftScores", source, requestIds],
		queryFn: async () => {
			const response = await backendService.getNurseScoresForShifts(requestIds, source);
			return response.data;
		},
		enabled: requestIds.length > 0,
	});
}

export function useSuggestedShifts(pinnedShiftIds?: string[]): { data: SuggestedShift[]; isLoading: boolean } {
	const { mergedRequests } = useRequestsContext();
	const { data: userShifts } = useUserShifts();
	const { userInfo } = useAuth();
	const { timezone } = useDepartmentFlags();

	const nurseInfo = userInfo?.nurses_info?.[0]?.nurse;
	const nurseId = nurseInfo?.id;

	const weeklyPreferences = nurseInfo?.employeeWeeklyPreferences ?? [];
	const specificPreferences = nurseInfo?.employeeSpecificPreferences ?? [];
	const avoidDates = nurseInfo?.avoidDates ?? [];

	const pinnedSet = useMemo(() => new Set(pinnedShiftIds ?? []), [pinnedShiftIds]);

	// Step 1: Hard filters (local, synchronous)
	const eligible = useMemo(() => {
		if (!mergedRequests?.length || !userShifts || !nurseId) return [];

		const now = DateTime.now();
		const twoWeeksLater = now.plus({ days: TWO_WEEKS_DAYS });

		return mergedRequests.filter((shift) => {
			// Always include pinned shifts (already applied) so they remain visible as "Pending"
			if (pinnedSet.has(shift.id)) return true;
			if (shift.startTime < now || shift.startTime > twoWeeksLater) return false;
			if (shift.myStatus) return false;
			return !(
				hasScheduleConflict(shift, userShifts, timezone) ||
				isOnAvoidDate(shift, specificPreferences, avoidDates, timezone) ||
				isOnWeeklyAvoid(shift, weeklyPreferences, timezone)
			);
		});
	}, [mergedRequests, userShifts, nurseId, timezone, weeklyPreferences, specificPreferences, avoidDates, pinnedSet]);

	// Extract individual request IDs for the API call (handle merged shifts)
	const requestIds = useMemo(() => {
		const ids: string[] = [];
		for (const shift of eligible) {
			if (shift.is_merged && shift.original_shifts) {
				for (const original of shift.original_shifts) {
					ids.push(original.id);
				}
			} else {
				ids.push(shift.id);
			}
		}
		return ids;
	}, [eligible]);

	// Step 2: Fetch scores from backend (replaces local scoring)
	const { data: scoresData, isLoading } = useNurseShiftScores(requestIds, AgentSource.ShiftPickup);

	// Step 3: Filter by threshold, sort, take top 2. If pinned, return exactly those shifts.
	const suggestedShifts = useMemo(() => {
		const toSuggestedShift = (shift: OpenShiftType): SuggestedShift => ({
			id: shift.id,
			startTime: shift.startTime,
			endTime: shift.endTime,
			shiftType: getShiftIndexByShiftTimes({ startTime: shift.startTime, endTime: shift.endTime }) === 0 ? ShiftType.Day : ShiftType.Night,
			departmentId: shift.department_id,
			location: shift.location || undefined,
			bonus: shift.current_bonus ?? shift.bonus,
			isMerged: shift.is_merged,
			originalShifts: shift.original_shifts,
		});

		// When we have pinned IDs, return exactly those shifts (preserving original suggestions after apply/refresh)
		if (pinnedSet.size > 0) {
			return eligible
				.filter((shift) => pinnedSet.has(shift.id))
				.map(toSuggestedShift)
				.sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis());
		}

		if (!scoresData?.scores || eligible.length === 0) return [];

		const scored = eligible.map((shift) => {
			let score: number | null;

			if (shift.is_merged && shift.original_shifts) {
				// For merged shifts: take the max score among original shift IDs
				const originalScores = shift.original_shifts
					.map((openShift) => scoresData.scores[openShift.id])
					.filter((s): s is number => s !== undefined && s !== null);
				score = originalScores.length > 0 ? Math.max(...originalScores) : null;
			} else {
				score = scoresData.scores[shift.id] ?? null;
			}

			return { shift, score };
		});

		const eligible_scored = scored
			.filter((s): s is { shift: OpenShiftType; score: number } => s.score !== null && s.score >= SCORE_THRESHOLD)
			.sort((a, b) => {
				if (b.score !== a.score) return b.score - a.score;
				return a.shift.startTime.toMillis() - b.shift.startTime.toMillis();
			});

		// Prefer highest-scored shifts within the next 10 days, fallback to overall highest
		const now = DateTime.now();
		const windowCutoff = now.plus({ days: SUGGESTION_WINDOW_DAYS });
		const withinWindow = eligible_scored.filter((s) => s.shift.startTime <= windowCutoff);
		const passing = (withinWindow.length > 0 ? withinWindow : eligible_scored).slice(0, MAX_SUGGESTIONS);

		return passing.map(({ shift }) => toSuggestedShift(shift)).sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis());
	}, [scoresData, eligible, pinnedSet]);

	return { data: suggestedShifts, isLoading };
}
